import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchWorkshops, setPage } from '../store/slices/workshopsSlice';
import Pagination from '../components/common/Pagination';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';

const WorkshopsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const { items: workshops, pagination, loading, error } = useSelector((state) => state.workshops);

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1');
    const contentType = searchParams.get('contentType') || '';
    const search = searchParams.get('search') || '';

    setContentTypeFilter(contentType);
    dispatch(setPage(page));
    dispatch(fetchWorkshops({ page, contentType, search }));
    dispatch(fetchSEO('workshops'));
  }, [searchParams, dispatch]);

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleFilterChange = (filterType) => {
    const params = new URLSearchParams(searchParams);
    if (filterType === contentTypeFilter) {
      params.delete('contentType');
      setContentTypeFilter('');
    } else {
      params.set('contentType', filterType);
      setContentTypeFilter(filterType);
    }
    params.set('page', '1'); // Reset to first page
    setSearchParams(params);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatPrice = (price, currency) => {
    if (!price) return 'Free';
    return `${currency} ${price}`;
  };

  const getContentTypeBadge = (contentType) => {
    switch (contentType) {
      case 'live':
        return { text: '🔴 Live', color: 'bg-red-100 text-red-800' };
      case 'recorded':
        return { text: '📹 Recorded', color: 'bg-blue-100 text-blue-800' };
      case 'both':
        return { text: '🔴📹 Live + Recorded', color: 'bg-purple-100 text-purple-800' };
      default:
        return { text: 'Workshop', color: 'bg-gray-100 text-gray-800' };
    }
  };

  return (
    <>
      <SEO
        page="workshops"
        title="Workshops - Learn & Grow"
        description="Join our workshops on art, Vastu, and home decor. Learn from experts through live sessions and recorded content."
        keywords="workshops, art workshops, vastu workshops, home decor classes, online workshops"
        url="/workshops"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-black mb-2">Our Workshops</h1>
          <p className="text-gray-600">Learn and grow with our expert-led sessions</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => handleFilterChange('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              contentTypeFilter === ''
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleFilterChange('live')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              contentTypeFilter === 'live'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🔴 Live
          </button>
          <button
            onClick={() => handleFilterChange('recorded')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              contentTypeFilter === 'recorded'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📹 Recorded
          </button>
          <button
            onClick={() => handleFilterChange('both')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              contentTypeFilter === 'both'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🔴📹 Both
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">Error loading workshops: {error}</p>
            <p className="text-sm text-red-500 mt-2">
              Please check your connection and ensure the backend is running.
            </p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="w-full h-48 bg-gray-200 rounded mb-4" />
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : workshops.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {workshops.map((workshop) => {
                const badge = getContentTypeBadge(workshop.contentType);
                return (
                  <Link
                    key={workshop._id}
                    to={`/workshops/${workshop._id}`}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
                  >
                    {workshop.image && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={workshop.image}
                          alt={workshop.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute top-2 right-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                            {badge.text}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition">
                        {workshop.title}
                      </h3>
                      {workshop.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                          {workshop.description}
                        </p>
                      )}
                      <div className="space-y-2 text-sm text-gray-600 mb-3">
                        {workshop.date && (
                          <div className="flex items-center">
                            <span className="mr-2">📅</span>
                            <span>{formatDate(workshop.date)}</span>
                            {workshop.time && <span className="ml-2">at {formatTime(workshop.time)}</span>}
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className="mr-2">💰</span>
                          <span className="font-semibold text-primary">
                            {formatPrice(workshop.price, workshop.currency)}
                          </span>
                        </div>
                        {workshop.instructor && (
                          <div className="flex items-center">
                            <span className="mr-2">👤</span>
                            <span>{workshop.instructor}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <span className="inline-block px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium group-hover:bg-primary-dark transition">
                          View Details
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg mb-2">No workshops available yet. Check back soon!</p>
            {!error && (
              <p className="text-sm text-gray-500">
                Note: Only published workshops are visible. Make sure workshops are marked as published in the admin panel.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default WorkshopsPage;

